import React from 'react';
import { View } from 'react-native';

import type { MatchProbabilities } from '@/features/predictions/model';
import { localizeDigits } from '@/lib/i18n/format';
import { useLocale } from '@/lib/i18n/LocaleProvider';

import { AppText } from './AppText';

/**
 * Volt 3-segment 1X2 bar: volt for the pick side, white 25% / 12% for the
 * rest (per the Volt handoff). Labels are home / draw / away percentages.
 */
export function ProbabilityBar({
  probabilities,
  homeLabel,
  drawLabel,
  awayLabel,
}: {
  probabilities: MatchProbabilities;
  homeLabel: string;
  drawLabel: string;
  awayLabel: string;
}) {
  const { arabicNumerals } = useLocale();
  const pct = (value: number) => localizeDigits(`${value}%`, arabicNumerals);
  const homeIsPick = probabilities.pick === 'home';
  const awayIsPick = probabilities.pick === 'away';

  return (
    <View className="gap-1.5">
      <View className="flex-row items-center justify-between">
        <AppText
          variant="micro"
          weight="bold"
          tabular
          className={homeIsPick ? 'text-volt-bright' : 'text-muted'}
        >
          {homeLabel} {pct(probabilities.home)}
        </AppText>
        <AppText variant="micro" weight="medium" tabular className="text-muted">
          {drawLabel} {pct(probabilities.draw)}
        </AppText>
        <AppText
          variant="micro"
          weight="bold"
          tabular
          className={awayIsPick ? 'text-volt-bright' : 'text-muted'}
        >
          {pct(probabilities.away)} {awayLabel}
        </AppText>
      </View>
      <View className="h-1.5 flex-row" style={{ gap: 3 }}>
        <View
          className={`rounded-full ${homeIsPick ? 'bg-volt' : 'bg-white/25'}`}
          style={{ flex: Math.max(probabilities.home, 2) }}
        />
        <View className="rounded-full bg-white/15" style={{ flex: Math.max(probabilities.draw, 2) }} />
        <View
          className={`rounded-full ${awayIsPick ? 'bg-volt' : 'bg-white/25'}`}
          style={{ flex: Math.max(probabilities.away, 2) }}
        />
      </View>
    </View>
  );
}
